import { useParams, useNavigate, Link } from 'react-router';
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { appointmentsApi } from '@/api/appointments';
import { customersApi, type CustomerAppointment } from '@/api/customers';
import { useBusinessId } from '@/hooks/useBusinessId';
import { formatTime } from '@/utils/time';
import { formatDateShort } from '@/utils/date';
import { StatusBadge, type StatusType } from '../components/status-badge';
import { Star, Pencil, X, Clock, ChevronLeft, History, Dna, User, Loader2, AlertTriangle, FileCheck2, FileText } from 'lucide-react';

/** Map backend stateCode to the StatusBadge type used by the UI */
function mapStateCode(stateCode: string): StatusType {
  switch (stateCode) {
    case 'CONFIRMED': return 'confirmed';
    case 'COMPLETED': return 'completed';
    case 'CANCELLED': return 'cancelled';
    case 'NO_SHOW': return 'noshow';
    case 'EXPIRED': return 'expired';
    case 'PENDING': return 'pending';
    case 'IN_PROGRESS': return 'in-progress';
    default: return 'pending';
  }
}

export function AppointmentView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const businessId = useBusinessId();
  const queryClient = useQueryClient();

  const [starred, setStarred] = useState(false);
  const [showHistorySheet, setShowHistorySheet] = useState(false);
  const [historyFilter, setHistoryFilter] = useState<'all' | 'mine'>('all');

  const appointmentId = Number(id);

  // Fetch the appointment
  const { data: appointment, isLoading: appointmentLoading, isError: appointmentError } = useQuery({
    queryKey: ['appointment', businessId, appointmentId],
    queryFn: () => appointmentsApi.getById(businessId, appointmentId),
    enabled: !!businessId && !!appointmentId,
  });

  // Fetch customer history once we have customerId
  const { data: customerHistory = [] } = useQuery({
    queryKey: ['customerAppointments', businessId, appointment?.customerId],
    queryFn: () => customersApi.getAppointments(businessId, appointment!.customerId),
    enabled: !!businessId && !!appointment?.customerId,
  });

  // State transition mutation
  const transitionMutation = useMutation({
    mutationFn: (args: { stateCode: "CONFIRMED" | "COMPLETED" | "CANCELLED" | "NO_SHOW" | "PENDING"; cancelledBy?: "CUSTOMER" | "BUSINESS"; cancellationReason?: string }) =>
      appointmentsApi.transitionState(businessId, appointmentId, args),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['appointment', businessId, appointmentId] });
      queryClient.invalidateQueries({ queryKey: ['customerAppointments', businessId, appointment?.customerId] });
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      const msg = vars.stateCode === 'COMPLETED' ? 'Cita completada'
        : vars.stateCode === 'CONFIRMED' ? 'Cita confirmada'
        : vars.stateCode === 'CANCELLED' ? 'Cita cancelada'
        : 'Estado actualizado';
      toast.success(msg);
    },
    onError: () => toast.error('No se pudo actualizar la cita'),
  });

  if (appointmentLoading) {
    return (
      <div className="h-full flex items-center justify-center" style={{ backgroundColor: 'var(--cream)' }}>
        <Loader2 size={32} className="animate-spin" style={{ color: 'var(--terracotta)' }} />
      </div>
    );
  }

  if (appointmentError || !appointment) {
    return <div className="p-6" style={{ color: 'var(--dark-teal)' }}>Cita no encontrada</div>;
  }

  const uiStatus = mapStateCode(appointment.stateCode);

  const getInitials = (name: string) =>
    name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  const getPrimaryAction = () => {
    if (transitionMutation.isPending) {
      return { label: 'Procesando...', bg: 'var(--muted-bg)', color: 'var(--muted-fg)', disabled: true, action: () => {} };
    }
    switch (appointment.stateCode) {
      case 'PENDING':
        return {
          label: 'Confirmar',
          bg: 'var(--terracotta)',
          color: 'white',
          disabled: false,
          action: () => transitionMutation.mutate({ stateCode: 'CONFIRMED' }),
        };
      case 'CONFIRMED':
        return {
          label: 'Completar',
          bg: 'var(--dark-teal)',
          color: 'white',
          disabled: false,
          action: () => transitionMutation.mutate({ stateCode: 'COMPLETED' }),
        };
      case 'COMPLETED':
        return { label: 'Completada', bg: 'var(--muted-bg)', color: 'var(--muted-fg)', disabled: true, action: () => {} };
      case 'CANCELLED':
        return { label: 'Cancelada', bg: 'var(--muted-bg)', color: 'var(--muted-fg)', disabled: true, action: () => {} };
      case 'NO_SHOW':
        return { label: 'No asisti\u00f3', bg: 'var(--muted-bg)', color: 'var(--muted-fg)', disabled: true, action: () => {} };
      case 'EXPIRED':
        return { label: 'Expirada', bg: 'var(--muted-bg)', color: 'var(--muted-fg)', disabled: true, action: () => {} };
      default:
        return {
          label: 'Confirmar',
          bg: 'var(--terracotta)',
          color: 'white',
          disabled: false,
          action: () => transitionMutation.mutate({ stateCode: 'CONFIRMED' }),
        };
    }
  };

  const primaryAction = getPrimaryAction();

  const handleCancel = () => {
    if (window.confirm(`Cancelar cita de ${appointment.customerName}?`)) {
      transitionMutation.mutate({ stateCode: 'CANCELLED', cancelledBy: 'BUSINESS' });
    }
  };

  // Filter customer history for the bottom sheet
  const filteredHistory = customerHistory.filter((apt: CustomerAppointment) => {
    if (historyFilter === 'mine') return apt.userName === appointment.userName;
    return true;
  });

  // Completed visits for summary
  const completedVisits = customerHistory.filter((a: CustomerAppointment) => a.stateCode === 'COMPLETED');

  // Service name: prefer services array if present, else serviceName
  const serviceName = appointment.services && appointment.services.length > 0
    ? appointment.services.map(s => s.serviceName || s.servicePackageName).filter(Boolean).join(', ')
    : appointment.serviceName;

  /* --- Navigation strip --- */
  const navStrip = (
    <div
      className="flex items-center justify-between px-4 md:px-5 flex-shrink-0"
      style={{ height: '40px', borderBottom: '1px solid var(--border-color)', backgroundColor: 'var(--off-white)' }}
    >
      <button onClick={() => navigate(-1)} className="flex items-center gap-1"
        style={{ fontSize: '13px', color: 'var(--muted-fg)', cursor: 'pointer', minHeight: '36px' }}>
        <ChevronLeft size={14} /><span>Volver</span>
      </button>
      <span style={{ fontSize: '12px', color: 'var(--muted-fg)' }}>Detalle de cita</span>
      <div style={{ width: '60px' }} />
    </div>
  );

  /* --- Shared action buttons builder --- */
  const renderActionButtons = (btnHeight: string, iconSize: number) => (
    <>
      <button disabled={primaryAction.disabled} className="flex-1 rounded-lg transition-colors flex items-center justify-center gap-2"
        style={{ height: btnHeight, backgroundColor: primaryAction.bg, color: primaryAction.color, fontFamily: 'var(--font-body)', fontSize: btnHeight === '38px' ? '13px' : '14px', fontWeight: 500, cursor: primaryAction.disabled ? 'not-allowed' : 'pointer', borderRadius: 'var(--radius-md)' }}
        onClick={primaryAction.action}>
        {transitionMutation.isPending && <Loader2 size={14} className="animate-spin" />}
        {primaryAction.label}
      </button>
      <button onClick={() => setStarred(!starred)} className="flex items-center justify-center rounded-lg transition-colors" title="Favorito"
        style={{ width: btnHeight, height: btnHeight, border: `1px solid ${starred ? 'var(--terracotta)' : 'var(--border-color)'}`, backgroundColor: starred ? 'rgba(224, 123, 84, 0.08)' : 'transparent', borderRadius: 'var(--radius-md)' }}>
        <Star size={iconSize} fill={starred ? 'var(--terracotta)' : 'none'} stroke={starred ? 'var(--terracotta)' : 'var(--muted-fg)'} strokeWidth={1.5} />
      </button>
      <button onClick={() => navigate(`/appointment/${appointment.id}/edit`)} className="flex items-center justify-center rounded-lg transition-colors hover:bg-[#FAF9F7]" title="Editar"
        style={{ width: btnHeight, height: btnHeight, border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)' }}>
        <Pencil size={iconSize - 1} stroke="var(--muted-fg)" strokeWidth={1.5} />
      </button>
      <button onClick={handleCancel} disabled={appointment.stateCode === 'CANCELLED' || appointment.stateCode === 'COMPLETED'}
        className="flex items-center justify-center rounded-lg transition-colors hover:bg-red-50" title="Cancelar"
        style={{ width: btnHeight, height: btnHeight, border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', cursor: (appointment.stateCode === 'CANCELLED' || appointment.stateCode === 'COMPLETED') ? 'not-allowed' : 'pointer', opacity: (appointment.stateCode === 'CANCELLED' || appointment.stateCode === 'COMPLETED') ? 0.4 : 1 }}>
        <X size={iconSize} stroke="var(--destructive)" strokeWidth={1.5} />
      </button>
    </>
  );

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: 'var(--cream)' }}>
      {navStrip}

      {/* MOBILE LAYOUT (< 768px) */}
      <div className="md:hidden flex-1 flex flex-col overflow-hidden">
        <div className="flex-1 overflow-auto pb-24">
          {/* Compact Client Header */}
          <div style={{ backgroundColor: 'var(--dark-teal)', padding: '14px 16px' }}>
            <div className="flex items-center gap-3">
              <div className="rounded-full flex items-center justify-center flex-shrink-0" style={{ width: '44px', height: '44px', backgroundColor: 'var(--terracotta)' }}>
                <span className="text-white" style={{ fontFamily: 'var(--font-heading)', fontSize: '16px' }}>{getInitials(appointment.customerName)}</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-white truncate" style={{ fontFamily: 'var(--font-heading)', fontSize: '16px' }}>{appointment.customerName}</span>
                  <Link to={`/customer/${appointment.customerId}/history`} className="flex items-center gap-1 rounded-full flex-shrink-0"
                    style={{ padding: '2px 8px', fontSize: '10px', fontWeight: 500, color: 'rgba(255,255,255,0.7)', backgroundColor: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)' }}>
                    <History size={10} /> Historial
                  </Link>
                  <Link to={`/client-dna/${appointment.customerId}`} className="flex items-center gap-1 rounded-full flex-shrink-0"
                    style={{ padding: '2px 8px', fontSize: '10px', fontWeight: 500, color: 'rgba(255,255,255,0.7)', backgroundColor: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)' }}>
                    <Dna size={10} /> DNA
                  </Link>
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)' }}>{completedVisits.length} visitas</span>
                </div>
              </div>
            </div>
          </div>

          {/* Service + Details */}
          <div style={{ padding: '12px 14px 24px' }}>
            {/* Service Card */}
            <div className="rounded-xl" style={{
              backgroundColor: 'var(--off-white)',
              border: uiStatus === 'in-progress' ? '2px solid var(--terracotta)' : '1px solid var(--border-color)',
              boxShadow: uiStatus === 'in-progress' ? '0 2px 12px rgba(224, 123, 84, 0.12)' : 'var(--shadow-card)',
              padding: '14px 16px', marginBottom: '10px'
            }}>
              <div className="flex items-start justify-between mb-1.5">
                <div style={{ fontFamily: 'var(--font-heading)', fontSize: '17px', color: 'var(--dark-teal)' }}>
                  {serviceName}
                </div>
                <StatusBadge status={uiStatus} />
              </div>
              <div className="flex items-center gap-2 flex-wrap" style={{ fontSize: '12px', color: 'var(--muted-fg)' }}>
                <span className="flex items-center gap-1"><Clock size={12} />{formatTime(appointment.startTime)}</span>
                <span style={{ opacity: 0.3 }}>·</span>
                <span>{appointment.duration} min</span>
                <span style={{ opacity: 0.3 }}>·</span>
                <span className="flex items-center gap-1"><User size={11} />{appointment.userName}</span>
              </div>
              {/* Action buttons */}
              <div className="flex items-center gap-2 mt-3 pt-3" style={{ borderTop: '1px solid var(--border-color)' }}>
                {renderActionButtons('38px', 15)}
              </div>
            </div>

            {/* Recent Visits */}
            <div className="rounded-xl" style={{ backgroundColor: 'var(--off-white)', border: '1px solid var(--border-color)', boxShadow: 'var(--shadow-card)', padding: '12px 14px', marginBottom: '10px' }}>
              <div className="flex items-center justify-between" style={{ marginBottom: '6px' }}>
                <div className="section-label">Visitas recientes</div>
                <button onClick={() => setShowHistorySheet(true)} style={{ fontSize: '12px', color: 'var(--terracotta)', fontWeight: 500, cursor: 'pointer' }}>Ver todo</button>
              </div>
              {completedVisits.length === 0 ? (
                <div style={{ fontSize: '12px', color: 'var(--muted-fg)' }}>Sin visitas previas</div>
              ) : (
                <div className="space-y-0">
                  {completedVisits.slice(0, 3).map((apt: CustomerAppointment, i: number) => (
                    <div key={apt.id} style={{ padding: '4px 0', borderBottom: i < Math.min(completedVisits.length, 3) - 1 ? '1px solid var(--border-color)' : 'none' }}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="truncate min-w-0" style={{ fontSize: '12px', color: 'var(--dark-teal)' }}>{apt.serviceName}</span>
                        <span className="flex-shrink-0" style={{ fontSize: '11px', color: 'var(--muted-fg)' }}>{formatDateShort(apt.appointmentDate)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* DESKTOP/TABLET LAYOUT (>= 768px) */}
      <div className="hidden md:flex flex-1 overflow-hidden">
        {/* LEFT COLUMN -- Client Spotlight */}
        <div className="flex flex-col overflow-auto" style={{ width: '340px', minWidth: '340px', backgroundColor: 'var(--dark-teal)', padding: '24px 20px 16px' }}>
          <div className="flex flex-col items-center mb-4">
            <div className="rounded-full flex items-center justify-center" style={{ width: '88px', height: '88px', backgroundColor: 'var(--terracotta)' }}>
              <span className="text-white" style={{ fontFamily: 'var(--font-heading)', fontSize: '28px' }}>{getInitials(appointment.customerName)}</span>
            </div>
          </div>

          <div className="text-center mb-4">
            <div className="text-white mb-1.5" style={{ fontFamily: 'var(--font-heading)', fontSize: '20px' }}>{appointment.customerName}</div>
            <div className="flex items-center justify-center gap-2 mb-2">
              <Link to={`/customer/${appointment.customerId}/history`} className="flex items-center gap-1 rounded-full transition-colors hover:bg-white/10"
                style={{ padding: '2px 8px', fontSize: '10px', fontWeight: 500, color: 'rgba(255,255,255,0.7)', backgroundColor: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)' }}>
                <History size={10} /> Historial
              </Link>
              <Link to={`/client-dna/${appointment.customerId}`} className="flex items-center gap-1 rounded-full transition-colors hover:bg-white/10"
                style={{ padding: '2px 8px', fontSize: '10px', fontWeight: 500, color: 'rgba(255,255,255,0.7)', backgroundColor: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)' }}>
                <Dna size={10} /> DNA
              </Link>
            </div>
          </div>

          <div className="rounded-lg mb-3" style={{ backgroundColor: 'rgba(255,255,255,0.06)', padding: '10px 12px' }}>
            <div className="space-y-2">
              <div className="flex justify-between" style={{ fontSize: '12px' }}>
                <span style={{ color: 'rgba(255,255,255,0.45)' }}>Total visitas</span>
                <span style={{ color: 'rgba(255,255,255,0.8)' }}>{completedVisits.length}</span>
              </div>
              <div className="flex justify-between" style={{ fontSize: '12px' }}>
                <span style={{ color: 'rgba(255,255,255,0.45)' }}>Profesional</span>
                <span style={{ color: 'rgba(255,255,255,0.8)' }}>{appointment.userName}</span>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN -- Appointment Details */}
        <div className="flex-1 flex flex-col overflow-auto" style={{ padding: '20px 24px 16px' }}>
          <div className="rounded-xl mb-4" style={{
            backgroundColor: 'var(--off-white)',
            border: uiStatus === 'in-progress' ? '2px solid var(--terracotta)' : '1px solid var(--border-color)',
            boxShadow: uiStatus === 'in-progress' ? '0 2px 12px rgba(224, 123, 84, 0.12)' : 'var(--shadow-card)',
            padding: '16px 20px'
          }}>
            <div className="flex items-start justify-between mb-2">
              <div>
                <div style={{ fontFamily: 'var(--font-heading)', fontSize: '20px', color: 'var(--dark-teal)', marginBottom: '4px' }}>{serviceName}</div>
                <div className="flex items-center gap-3" style={{ fontSize: '13px', color: 'var(--muted-fg)' }}>
                  <span className="flex items-center gap-1"><Clock size={13} />{formatTime(appointment.startTime)}</span>
                  <span style={{ opacity: 0.3 }}>|</span>
                  <span>{appointment.duration} min</span>
                  <span style={{ opacity: 0.3 }}>|</span>
                  <span className="flex items-center gap-1"><User size={12} />{appointment.userName}</span>
                </div>
              </div>
              <StatusBadge status={uiStatus} />
            </div>

            {/* Action buttons */}
            <div className="flex items-center gap-2 mt-3 pt-3" style={{ borderTop: '1px solid var(--border-color)' }}>
              {renderActionButtons('42px', 16)}
            </div>
          </div>

          {/* Cancellation info */}
          {appointment.stateCode === 'CANCELLED' && appointment.cancellationReason && (
            <div className="rounded-xl mb-4" style={{ backgroundColor: 'var(--off-white)', border: '1px solid var(--border-color)', boxShadow: 'var(--shadow-card)', padding: '14px 20px' }}>
              <div className="section-label" style={{ marginBottom: '6px' }}>Motivo de cancelaci\u00f3n</div>
              <div style={{ fontSize: '14px', color: 'var(--dark-teal)', lineHeight: 1.55 }}>
                {appointment.cancelledBy === 'CUSTOMER' ? 'Cancelado por el cliente' : 'Cancelado por el negocio'}
                {appointment.cancellationReason && ` — ${appointment.cancellationReason}`}
              </div>
            </div>
          )}

          {/* Consent documents */}
          {appointment.consents && appointment.consents.length > 0 && (() => {
            const pending = appointment.consents!.filter(c => c.status === 'PENDING');
            return (
              <>
                {pending.length > 0 && (
                  <div className="rounded-xl flex items-start gap-2.5 mb-4" style={{
                    backgroundColor: '#FFFBEB', border: '1px solid #FDE68A',
                    padding: '14px 20px'
                  }}>
                    <AlertTriangle size={16} style={{ color: '#D97706', marginTop: '1px', flexShrink: 0 }} />
                    <span className="min-w-0 break-words" style={{ fontSize: '14px', color: '#92400E', lineHeight: 1.5 }}>
                      El cliente no ha firmado: {pending.map(c => c.consentFormName).join(', ')}
                    </span>
                  </div>
                )}
                <div className="rounded-xl mb-4" style={{
                  backgroundColor: 'var(--off-white)', border: '1px solid var(--border-color)',
                  boxShadow: 'var(--shadow-card)', padding: '14px 20px'
                }}>
                  <div className="section-label" style={{ marginBottom: '8px' }}>Consentimientos</div>
                  <div className="space-y-2">
                    {appointment.consents!.map(consent => (
                      <div key={consent.id} className="flex items-center justify-between" style={{ fontSize: '13px' }}>
                        <div className="flex items-center gap-2 min-w-0">
                          {consent.status === 'SIGNED'
                            ? <FileCheck2 size={15} style={{ color: '#252E3E', flexShrink: 0 }} />
                            : <FileText size={15} style={{ color: '#D97706', flexShrink: 0 }} />
                          }
                          <span className="truncate" style={{ color: 'var(--dark-teal)' }}>
                            {consent.consentFormName}
                          </span>
                        </div>
                        <span style={{
                          fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '9999px',
                          ...(consent.status === 'SIGNED'
                            ? { backgroundColor: 'rgba(37, 46, 62, 0.08)', color: '#252E3E' }
                            : { backgroundColor: '#FFFBEB', color: '#92400E' })
                        }}>
                          {consent.status === 'SIGNED' ? 'Firmado' : 'Pendiente'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            );
          })()}

          {/* Services breakdown if multiple */}
          {appointment.services && appointment.services.length > 1 && (
            <div className="rounded-xl mb-4" style={{ backgroundColor: 'var(--off-white)', border: '1px solid var(--border-color)', boxShadow: 'var(--shadow-card)', padding: '14px 20px' }}>
              <div className="section-label" style={{ marginBottom: '8px' }}>Servicios</div>
              <div className="space-y-2">
                {appointment.services.map((s, i) => (
                  <div key={i} className="flex items-center justify-between" style={{ fontSize: '13px' }}>
                    <span style={{ color: 'var(--dark-teal)' }}>{s.serviceName || s.servicePackageName}</span>
                    <span style={{ color: 'var(--muted-fg)' }}>{s.durationMinutes} min</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="rounded-xl mb-4 flex-1" style={{ backgroundColor: 'var(--off-white)', border: '1px solid var(--border-color)', boxShadow: 'var(--shadow-card)', padding: '14px 20px', minHeight: 0 }}>
            <div className="flex items-center justify-between" style={{ marginBottom: '8px' }}>
              <div className="section-label">Visitas recientes</div>
              <button onClick={() => setShowHistorySheet(true)} style={{ fontSize: '12px', color: 'var(--terracotta)', fontWeight: 500, cursor: 'pointer' }}>Ver todo</button>
            </div>
            <div className="space-y-0">
              {completedVisits.slice(0, 4).map((apt: CustomerAppointment, i: number) => (
                <div key={apt.id} className="flex items-center justify-between"
                  style={{ padding: '6px 0', borderBottom: i < Math.min(completedVisits.length, 4) - 1 ? '1px solid var(--border-color)' : 'none' }}>
                  <div className="flex items-center gap-3">
                    <span style={{ fontSize: '12px', color: 'var(--muted-fg)', width: '70px' }}>{formatDateShort(apt.appointmentDate)}</span>
                    <span style={{ fontSize: '13px', color: 'var(--dark-teal)' }}>{apt.serviceName}</span>
                  </div>
                  <span style={{ fontSize: '12px', color: 'var(--muted-fg)' }}>{apt.userName}</span>
                </div>
              ))}
              {completedVisits.length === 0 && (
                <div style={{ fontSize: '13px', color: 'var(--muted-fg)', padding: '8px 0' }}>Sin visitas previas</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Customer History Bottom Sheet */}
      {showHistorySheet && (
        <>
          <div className="fixed inset-0 bg-black/40 z-[100]" onClick={() => setShowHistorySheet(false)} />
          <div className="fixed left-0 right-0 bottom-0 z-[101]"
            style={{ height: '65vh', backgroundColor: 'var(--off-white)', borderTopLeftRadius: '20px', borderTopRightRadius: '20px', boxShadow: 'var(--shadow-bottom-sheet)' }}>
            <div className="flex justify-center pt-3 pb-2">
              <div style={{ width: '40px', height: '4px', borderRadius: '2px', backgroundColor: 'var(--border-color)' }} />
            </div>
            <div className="px-4 md:px-6 py-3" style={{ borderBottom: '1px solid var(--border-color)' }}>
              <div className="flex items-center justify-between">
                <div>
                  <div style={{ fontFamily: 'var(--font-heading)', fontSize: '17px', color: 'var(--dark-teal)' }}>{appointment.customerName} -- Todas las visitas</div>
                  <div style={{ fontSize: '12px', color: 'var(--muted-fg)' }}>{filteredHistory.length} citas</div>
                </div>
                <div className="flex rounded-lg overflow-hidden" style={{ border: '1px solid var(--border-color)' }}>
                  <button onClick={() => setHistoryFilter('all')} className="px-3 py-1"
                    style={{ fontSize: '12px', backgroundColor: historyFilter === 'all' ? 'var(--terracotta)' : 'transparent', color: historyFilter === 'all' ? 'white' : 'var(--dark-teal)' }}>
                    Todo el equipo
                  </button>
                  <button onClick={() => setHistoryFilter('mine')} className="px-3 py-1"
                    style={{ fontSize: '12px', backgroundColor: historyFilter === 'mine' ? 'var(--terracotta)' : 'transparent', color: historyFilter === 'mine' ? 'white' : 'var(--dark-teal)' }}>
                    Solo m\u00edas
                  </button>
                </div>
              </div>
            </div>
            <div className="overflow-auto" style={{ height: 'calc(65vh - 100px)' }}>
              {filteredHistory.map((apt: CustomerAppointment, index: number) => (
                <div key={apt.id}>
                  <div className="px-4 md:px-6 py-3 flex items-center justify-between">
                    <div className="flex-1">
                      <div style={{ fontSize: '12px', color: 'var(--muted-fg)', marginBottom: '2px' }}>{formatDateShort(apt.appointmentDate)} a las {formatTime(apt.startTime)}</div>
                      <div style={{ fontSize: '14px', color: 'var(--dark-teal)' }}>{apt.serviceName}</div>
                      <div style={{ fontSize: '12px', color: 'var(--muted-fg)' }}>{apt.userName}</div>
                    </div>
                    <StatusBadge status={mapStateCode(apt.stateCode)} size="sm" />
                  </div>
                  {index < filteredHistory.length - 1 && (
                    <div className="mx-4 md:mx-6" style={{ borderBottom: '1px solid var(--border-color)' }} />
                  )}
                </div>
              ))}
              {filteredHistory.length === 0 && (
                <div className="px-4 md:px-6 py-8 text-center" style={{ fontSize: '13px', color: 'var(--muted-fg)' }}>Sin visitas registradas</div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
